import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { FileText, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatCPFOrCNPJ,
  validateCPFOrCNPJ,
  sanitizeCPFOrCNPJ,
} from '@/lib/validators';
import { useAuth } from '@/contexts/AuthContext.hook';
import { TermsContent } from './TermsContent';
import { DynamicTermsViewer } from './DynamicTermsViewer';
import { getEspecialidadeConfig } from '@/utils/adminEspecializadoConfig';
import type { DiligenciaEspecialidade } from '@/utils/diligenciaChecklists';

/**
 * Phase 9 dynamic terms: when present, replaces the legacy <TermsContent />
 * (text-based JSX) with a PDF viewer. Null = legacy fallback path preserved.
 */
export interface CurrentTermsDocumentMeta {
  id: string;
  version_label: string;
  pdf_hash: string;
  pdf_storage_path: string;
}

interface TermsModalProps {
  open: boolean;
  accepting: boolean;
  onAccept: (cpfCnpj: string) => Promise<void>;
  /**
   * Phase 9: dynamic terms document metadata. When non-null, the modal
   * renders <DynamicTermsViewer> instead of the legacy <TermsContent />.
   * When null (no version published yet OR hook still loading), falls
   * back to the legacy text-based content — all other regras intact.
   */
  currentDocument?: CurrentTermsDocumentMeta | null;
}

const MIN_READ_TIME_MS = 15_000; // 15 segundos

export const TermsModal: React.FC<TermsModalProps> = ({
  open,
  accepting,
  onAccept,
  currentDocument = null,
}) => {
  const { profile, signOut, isAdmin } = useAuth();

  // Detectar admin especializado para contextualizar termos
  const espValue = profile?.diligencia_especialidade as DiligenciaEspecialidade | null | undefined;
  const espConfig = isAdmin && espValue ? getEspecialidadeConfig(espValue) : null;

  // Estado do formulário
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const openTimestamp = useRef<number>(0);

  // Pré-preencher CPF/CNPJ do perfil
  useEffect(() => {
    if (open && profile?.cpf) {
      setCpfCnpj(formatCPFOrCNPJ(profile.cpf));
    }
  }, [open, profile?.cpf]);

  // Timer mínimo de leitura
  useEffect(() => {
    if (!open) {
      setTimeElapsed(false);
      openTimestamp.current = 0;
      return;
    }

    openTimestamp.current = Date.now();
    const timer = setTimeout(() => setTimeElapsed(true), MIN_READ_TIME_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // Monitorar scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) {
      setScrollProgress(100);
      setHasReachedEnd(true);
      return;
    }

    const progress = Math.min((scrollTop / maxScroll) * 100, 100);
    setScrollProgress(progress);
    if (progress >= 90) {
      setHasReachedEnd(true);
    }
  }, []);

  // Validação do CPF/CNPJ
  const cleanDoc = sanitizeCPFOrCNPJ(cpfCnpj);
  const hasCpfCnpj = cleanDoc.length === 11 || cleanDoc.length === 14;
  const isDocValid = hasCpfCnpj && validateCPFOrCNPJ(cpfCnpj);
  const needsCpfCnpj = !profile?.cpf;

  // Condições para habilitar o botão
  const canAccept = hasReachedEnd && timeElapsed && agreed && isDocValid && !accepting;

  const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPFOrCNPJ(e.target.value);
    setCpfCnpj(formatted);
  };

  const handleAccept = async () => {
    if (!canAccept) return;
    try {
      await onAccept(cpfCnpj);
    } catch {
      // Erro já tratado pelo onError da mutation no hook
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-3xl h-[90vh] max-h-[800px] flex flex-col overflow-hidden p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-primary" />
            {espConfig ? espConfig.termoTitulo : 'Termos de Uso da Plataforma'}
          </DialogTitle>
          <DialogDescription>
            {espConfig
              ? espConfig.termoDescricao
              : 'Leia atentamente os Termos de Uso abaixo. Você precisa ler o documento completo e aceitar para continuar utilizando a plataforma.'}
          </DialogDescription>
        </DialogHeader>

        {/* Barra de progresso de leitura */}
        <div className="px-6 pt-3 pb-1 shrink-0">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Progresso de leitura</span>
            <span>{Math.round(scrollProgress)}%</span>
          </div>
          <Progress value={scrollProgress} className="h-1.5" />
        </div>

        {/* Conteúdo dos termos com scroll — flex-1 preenche o espaço restante */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          data-testid="terms-pdf-viewer"
          className="flex-1 min-h-0 mx-6 my-2 overflow-y-auto rounded-md border p-4 scrollbar-thin scrollbar-thumb-border"
        >
          {currentDocument ? (
            <DynamicTermsViewer
              storagePath={currentDocument.pdf_storage_path}
              scrollContainerRef={scrollRef}
              onScrollToEnd={() => setHasReachedEnd(true)}
            />
          ) : (
            <TermsContent />
          )}
        </div>

        {/* Formulário de aceite */}
        <div className="px-6 py-3 shrink-0 border-t space-y-3">
          {/* Campo CPF/CNPJ */}
          <div className="space-y-1.5">
            <Label htmlFor="terms-cpf-cnpj" className="text-sm font-medium">
              {needsCpfCnpj ? 'Informe seu CPF ou CNPJ para assinar' : 'CPF/CNPJ vinculado'}
            </Label>
            <Input
              id="terms-cpf-cnpj"
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              value={cpfCnpj}
              onChange={handleCpfCnpjChange}
              maxLength={18}
              disabled={!needsCpfCnpj || accepting}
              className={cn(
                hasCpfCnpj && !isDocValid && 'border-destructive focus-visible:ring-destructive'
              )}
            />
            {hasCpfCnpj && !isDocValid && (
              <p className="text-xs text-destructive">
                {cleanDoc.length === 11 ? 'CPF inválido' : 'CNPJ inválido'}
              </p>
            )}
          </div>

          {/* Checkbox de aceite */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="terms-agree"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              disabled={accepting}
            />
            <Label htmlFor="terms-agree" className="text-sm leading-snug cursor-pointer">
              {espConfig
                ? `Li e aceito os Termos de Uso e as condições de acesso como membro da equipe ${espConfig.label}`
                : 'Li e aceito integralmente os Termos de Uso da Plataforma Bravaforte'}
            </Label>
          </div>

          {/* Dicas de progresso */}
          {!hasReachedEnd && (
            <p className="text-xs text-muted-foreground">
              Role o documento até o final para habilitar o aceite.
            </p>
          )}
          {hasReachedEnd && !timeElapsed && (
            <p className="text-xs text-muted-foreground">
              Aguarde alguns segundos para garantir a leitura do documento...
            </p>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 pb-5 pt-2 shrink-0 flex-row gap-2 sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            disabled={accepting}
            className="text-muted-foreground"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Sair
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!canAccept}
          >
            {accepting ? 'Registrando...' : espConfig ? 'Aceitar e Continuar' : 'Aceitar Termos de Uso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
